import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectorRef, OnChanges, NgZone, OnDestroy, ChangeDetectionStrategy, SimpleChanges } from '@angular/core';
import { OrdersWithPayment, BotOrder, OccasionDetail } from 'src/app/models/order';
import { ScheduleBottomSheetComponent } from '../schedule-bottom-sheet/schedule-bottom-sheet-component';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { Store } from 'src/app/models/store';
import { OrderService } from 'src/app/order.service';
import { BooleanService } from 'src/app/_services/boolean.service';
import { PaymentOption } from 'src/app/_services/payment-option.service';
import { OccasionDetailService } from 'src/app/_services/occasion-detail.service';
import { Subscription } from 'rxjs';
import { AnalyticsService } from 'src/app/_services/analytics.service';

type OccasionCode = 'PICKUP' | 'CURBSIDE' | 'DELIVERY' | 'DINE IN' | 'TABLESIDE';
@Component({
  selector: 'app-order-summary-occasion',
  templateUrl: './occasion.component.html',
  styleUrls: ['./occasion.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OccasionComponent implements OnInit, OnChanges, OnDestroy {
  @Output() occasionChange = new EventEmitter<any>()
  @Input() hasPlacedTablesideOrder;
  @Input() orderWithPayment: OrdersWithPayment;
  @Input() paymentOption: PaymentOption;
  @Input() disabled: boolean = false;

  store: Store;
  occasionDetail: OccasionDetail;

  occasionDetailSub: Subscription;
  boolSub: Subscription;

  occasionLabels: Record<OccasionCode, string> = {
    PICKUP: 'Pickup',
    CURBSIDE: 'Curbside',
    DELIVERY: 'Delivery',
    'DINE IN': 'Dine In',
    TABLESIDE: 'Tableside'
  };

  orderedOccasions: OccasionCode[] = [];

  constructor(
    private scheduleBottomSheet: MatBottomSheet,
    private boolService: BooleanService,
    private occasionDetailService: OccasionDetailService,
    private cdr: ChangeDetectorRef,
    private analyticsService: AnalyticsService
  ) { }

  ngOnInit() {
    this.occasionDetailSub = this.occasionDetailService.current.subscribe(occasionDetail => {
      if (occasionDetail) {
        this.occasionDetail = occasionDetail;
        this.cdr.markForCheck();
      }
    })

    if (this.orderWithPayment) {
      this.store = this.orderWithPayment.store;
      this.occasionDetail = this.orderWithPayment.getOccasionDetail();
      this.orderedOccasions = this.computeOrderedVisibleOccasions();
    }

    this.boolSub = this.boolService.current.subscribe(data => {
      if (data && data.key === 'openSchedule' && data.value) {
        this.openBottomSheet(this.orderWithPayment?.bot_order.occasion);
        data.value = false;
        this.boolService.emitFlag(data);
        this.cdr.markForCheck();
      }
    })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['orderWithPayment'] && this.orderWithPayment) {
      this.store = this.orderWithPayment.store;
      this.occasionDetail = this.orderWithPayment.getOccasionDetail();
      this.orderedOccasions = this.computeOrderedVisibleOccasions();
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy() {
    this.occasionDetailSub.unsubscribe();
    this.boolSub.unsubscribe();
  }

  computeOrderedVisibleOccasions(): OccasionCode[] {
    if (!this.store) return [];

    // map legacy keys from occasion_list_order → canonical codes
    const MAP: Record<string, OccasionCode> = {
      has_order_occasion_pickup: 'PICKUP',
      has_order_occasion_curbside: 'CURBSIDE',
      has_order_occasion_delivery: 'DELIVERY',
      has_order_occasion_dine_in: 'DINE IN',
      has_order_occasion_tableside: 'TABLESIDE',
      // passthroughs if BE already sends canonical codes
      PICKUP: 'PICKUP',
      CURBSIDE: 'CURBSIDE',
      DELIVERY: 'DELIVERY',
      'DINE IN': 'DINE IN',
      TABLESIDE: 'TABLESIDE'
    };

    const DEFAULT: OccasionCode[] = ['PICKUP', 'DINE IN', 'TABLESIDE', 'DELIVERY', 'CURBSIDE'];

    let order: OccasionCode[] = [];
    const raw = this.store?.store_data?.occasion_list_order;

    if (raw) {
      try {
        const arr = JSON.parse(raw.replace(/'/g, '"'));
        if (Array.isArray(arr)) {
          order = Array.from(new Set(
            arr.map(k => MAP[k]).filter(Boolean)
          )) as OccasionCode[];
        }
      } catch { /* ignore bad JSON; fallback below */ }
    }

    const base = order.length ? order : DEFAULT;
    for (const k of DEFAULT) if (!base.includes(k)) base.push(k);
    const isCatering = this.orderWithPayment?.bot_order?.is_catering || false;
    const flags = (this.store as any)?.getOccasionFlags
      ? this.store.getOccasionFlags(isCatering)
      : (this.store as any)?.occasion_flags_online || {};
    return base.filter(code => !!flags[code]);
  }

  isValidDate(value: any): boolean {
    if (!value) return false;
    if (value instanceof Date) return !isNaN(value.getTime());
    // Handle moment objects (have toDate method)
    if (typeof value === 'object' && typeof value.toDate === 'function') {
      const d = value.toDate();
      return d instanceof Date && !isNaN(d.getTime());
    }
    // Handle date strings
    if (typeof value === 'string') {
      const d = new Date(value);
      return !isNaN(d.getTime());
    }
    return false;
  }

  get occasionsTableside(): OccasionCode[] {
    return this.orderedOccasions.includes('TABLESIDE') ? ['TABLESIDE'] : [];
  }

  get occasionsNonTableside(): OccasionCode[] {
    return this.orderedOccasions.filter(o => o !== 'TABLESIDE');
  }


  openBottomSheet(event): void {
    this.analyticsService.trackEvent('occasion_selected', {
      occasion_type: event,
      store_id: this.store?.id
    });
    this.occasionDetail.occasion = event;
    if (event === 'TABLESIDE') {
      this.occasionDetail.time = 'NOW';
    }
    let bsRef = this.scheduleBottomSheet.open(ScheduleBottomSheetComponent, {
      panelClass: 'schedule-bottom-sheet-container',
      disableClose: localStorage.getItem(`${this.orderWithPayment.bot_order.order_hash}_occasionInitial`) !== 'false',
      data: {
        orderWithPayment: this.orderWithPayment,
        paymentOption: this.paymentOption,
        occasionDetail: this.occasionDetail,
        hasPlacedTablesideOrder: this.hasPlacedTablesideOrder,
      }
    });

    bsRef.backdropClick().subscribe(event => {
      this.occasionDetail = this.orderWithPayment.getOccasionDetail()
    })

    bsRef.afterDismissed().subscribe(occasionDetail => {
      if (occasionDetail !== 'cancel') {
        this.occasionDetailService.updateOccasionDetail(occasionDetail);
      } else {
        this.occasionDetail = this.orderWithPayment.getOccasionDetail()
      }
    })
  }


}
